import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models (p203)
// ============================================================

export interface FP231 {
  fp231_01: string;
  fp231_02: number;
  fp231_11: string;  // 測試項目
  fp231_12: string;  // 測試方法
  fp231_13: string;  // 測試結果
  fp231_14: string;  // 標準值
  fp231_15: string;  // 判定 (P=PASS, F=FAIL)
  fp231_110: string; // server-derived classification
  fp231_112: string; // specification code
  fp231_121: string; // color-change result
  fp231_128: string; // back-cloth contamination result
  // Computed from F11A (populated by backend, read-only)
  f11a_15?: number;  // 輸出畫面格式 (1-5)
  f11a_11?: string;  // 測試項目中文名稱
}

export interface FP230 {
  fp230_01: string;         // 報告編號 (PK)
  fp230_11: string | null;  // 輸入日期
  fp230_12: string | null;  // 完成日期
  fp230_121: string;        // 建單人員
  fp230_13: string;         // 送件單號
  fp230_14: string;         // 缸號 (readOnly, from FP211_17)
  fp230_15: number;         // 送件項次
  fp230_16: string;         // 顏色 (readOnly, from FP211_11)
  fp230_17: string;         // 檢驗人員
  fp230_80: string;         // 核准狀態
  fp230_92: string | null;  // 存檔時間
  details?: FP231[];
}

export interface FP231Input {
  fp231_02?: number;
  fp231_11: string;
  fp231_12: string;
  fp231_13: string;
  fp231_14: string;
  fp231_15: string;
  fp231_112: string;
  fp231_121: string;
  fp231_128: string;
  // Display-only fields copied from FP231 for rendering purposes; not sent to backend
  f11a_15?: number;
  f11a_11?: string;
}

export interface CreateP203Request {
  fp230_11: string | null;  // 輸入日期
  fp230_12: string | null;  // 完成日期
  fp230_121: string;        // 建單人員
  fp230_13: string;         // 送件單號
  fp230_15: number;         // 送件項次
  fp230_17: string;         // 檢驗人員
  details: FP231Input[];
}

export interface UpdateP203Request extends CreateP203Request {}

export interface ListP203Response {
  data: FP230[];
  total: number;
  page: number;
  size: number;
}

export interface ValidationError {
  field: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

const BASE = '/api/p203';

export const p203Api = {
  async list(page = 1, size = 200, keyword?: string): Promise<ListP203Response> {
    const params: Record<string, string | number> = { page, size };
    if (keyword) params.keyword = keyword;
    const res = await apiClient.get(BASE, { params });
    return res.data;
  },

  async getByID(id: string): Promise<FP230> {
    const res = await apiClient.get(`${BASE}/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: CreateP203Request): Promise<FP230> {
    const res = await apiClient.post(BASE, data);
    return res.data;
  },

  async update(id: string, data: UpdateP203Request): Promise<FP230> {
    const res = await apiClient.put(`${BASE}/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
  },

  // Approval workflow
  async submitForApproval(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`${BASE}/${encodeURIComponent(id)}/submit`, { comment: comment || '' });
    return res.data;
  },

  async reject(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`${BASE}/${encodeURIComponent(id)}/reject`, { comment: comment || '' });
    return res.data;
  },

  async getApprovalHistory(id: string): Promise<ApprovalHistory[]> {
    const res = await apiClient.get(`${BASE}/${encodeURIComponent(id)}/approval-history`);
    return res.data.data || [];
  },
};

export interface ApprovalResponse {
  order_no: string;
  action: string;
  old_status: string;
  new_status: string;
  message: string;
}

export interface ApprovalHistory {
  id: number;
  order_no: string;
  action: string;
  actor_id: string;
  actor_name: string;
  comment: string;
  old_status: string;
  new_status: string;
  created_at: string;
}

export default p203Api;
